import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Button, Typography, Modal, IconButton, LinearProgress, Divider, CircularProgress, GlobalStyles, FormControl, InputLabel, Select, MenuItem, TextField, Grid, InputAdornment, FormHelperText} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PDFICON from '../../assets/Images/PDF.svg';
import axios from 'axios';
import { useSnackbar } from '../SnackbarContext';
import GlobalVariable from "../../services/Global";

const baseApiUrl = GlobalVariable.BASE_API_URL;

function buildFolderTree(fileMetas) {
  function insert(struct, folders, fileMeta) {
    if (!folders.length) {
      struct.files = struct.files || [];
      struct.files.push({
        file_name: fileMeta.filename
      });
      return;
    }
    const [head, ...tail] = folders;
    if (!struct.sub_folders) struct.sub_folders = [];
    let sub = struct.sub_folders.find(s => s.folder_name === head);
    if (!sub) {
      sub = { folder_name: head };
      struct.sub_folders.push(sub);
    }
    insert(sub, tail, fileMeta);
  }
  let root = fileMetas[0]?.folderStructure?.folders?.[0] || "Uploaded_Folder";
  const struct = { folder_name: root };
  fileMetas.forEach(meta =>
    insert(
      struct,
      meta.folderStructure.folders.slice(1),
      meta
    )
  );
  return struct;
}

const FileUploadModal = ({ open, onClose, rfpType, partnerName, onUploadSuccess }) => {
  const { showSnackbar } = useSnackbar();
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCloseDisabled, setIsCloseDisabled] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isFolderUpload, setIsFolderUpload] = useState(false);
  const [modalRfpType, setModalRfpType] = useState(rfpType && rfpType !== 'all' ? rfpType : '');
  const [modalPartnerName, setModalPartnerName] = useState(partnerName || '');
  const [rfpIssuer, setRfpIssuer] = useState('');
  const [rfpIssuersData, setRfpIssuersData] = useState(null);
  const [loadingIssuers, setLoadingIssuers] = useState(false);
  const [partnerNameError, setPartnerNameError] = useState(false);
  const [rfpIssuerError, setRfpIssuerError] = useState(false);

  // Update modalRfpType when rfpType prop changes
  useEffect(() => {
    if (rfpType && rfpType !== 'all') {
      setModalRfpType(rfpType);
    } else {
      setModalRfpType('');
    }
  }, [rfpType]);

  // Update modalPartnerName when partnerName prop changes
  useEffect(() => {
    setModalPartnerName(partnerName || '');
  }, [partnerName]);

  // Fetch RFP issuers when modal opens
  useEffect(() => {
    if (open) {
      const fetchRfpIssuers = async () => {
        try {
          setLoadingIssuers(true);
          const storedData = JSON.parse(localStorage.getItem('userDetails'));
          const accessToken = storedData?.access_token;
          
          const response = await axios.get(`${baseApiUrl}/getrfpissuer`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          
          // console.log('RFP Issuers API Response:', response.data);
          setRfpIssuersData(response.data);
          setLoadingIssuers(false);
        } catch (error) {
          console.error('Error fetching RFP issuers:', error);
          // console.log('Error response:', error.response?.data);
          setLoadingIssuers(false);
        }
      };
      
      fetchRfpIssuers();
    }
  }, [open]);

  const uploadTimer = useRef(null);

  const simulateFileUpload = useCallback((currentProgress = 0) => {
    uploadTimer.current = setInterval(() => {
      if (currentProgress < 90) {
        currentProgress += 5;
        setProgress(currentProgress);
      }
    }, 500);
  }, []);

  const uploadFiles = async () => {
    const storedData = JSON.parse(localStorage.getItem('userDetails'));
    const userid = storedData.userDetails.id;
    
    try {
      let folderTree;
      
      if (isFolderUpload) {
        // Build folder structure data for folder uploads
        const allFilesData = files.map((file, index) => {
          const folderPath = file.webkitRelativePath ? file.webkitRelativePath : file.name;
          const pathParts = folderPath.split('/');
          const folderStructure = {
            fullPath: folderPath,
            folders: pathParts.slice(0, -1),
            filename: pathParts[pathParts.length - 1],
            depth: pathParts.length - 1
          };
          return {
            filename: file.name,
            folderPath,
            folderStructure,
            fileSize: file.size,
            fileType: file.type,
            fileObj: file
          };
        });

        // Build folder tree structure for folders
        folderTree = buildFolderTree(allFilesData);
      } else {
        // For single file uploads, send empty folder structure
        folderTree = [];
      }
      
      // Create FormData
      const formData = new FormData();
      formData.append('user_id', userid);
      formData.append('rfp_type', modalRfpType || '');
      
      // Handle RFP Issuer and Partner Name logic
      if (rfpIssuer === 'Others') {
        // If "Others" is selected, send both rfp_issuer and partner_name
        formData.append('rfp_issuer', rfpIssuer);
        formData.append('partner_name', modalPartnerName || '');
      } else if (rfpIssuer && rfpIssuer.trim() !== '') {
        // If any other option is selected, send it as partner_name, don't send rfp_issuer
        formData.append('partner_name', rfpIssuer);
      } else {
        // If no RFP Issuer is selected, send partner_name as is
        formData.append('partner_name', modalPartnerName || '');
      }
      
      if (isFolderUpload) {
        formData.append('folder_structure', JSON.stringify(folderTree));
        formData.append('mode', 'folder');
        formData.append('upload_mode', 'folder');
      } else {
        formData.append('folder_structure', JSON.stringify([]));
        formData.append('mode', 'single');
        formData.append('upload_mode', 'single');
      }
      
      // Append all files to all_files key
      files.forEach((file) => {
        formData.append('all_files', file, file.name);
      });

      const response = await axios.post(`${baseApiUrl}/winning_rfp/upload_folder`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'accept': 'application/json'
        }
      });

      if (response.data.status_code === 200) {
        setProgress(100);
        showSnackbar(`${files.length} file(s) uploaded successfully.`, "success");
        resetState();
        if (onUploadSuccess) {
          onUploadSuccess();
        }
        return response.data;
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 500) {
        showSnackbar('Server error: File names not supported. Please change file names and upload again.', 'error');
      } else if (error.response?.status === 400) {
        showSnackbar('Bad request: Please check your file formats.', 'error');
      } else if (error.response?.status === 413) {
        showSnackbar('Files too large: Please upload smaller files.', 'error');
      } else {
        showSnackbar('Upload failed: Please try again.', 'error');
      }
      setProgress(0);
    } finally {
      setLoading(false);
      setUploading(false);
      clearInterval(uploadTimer.current);
      setIsCloseDisabled(false);
    }
  };

  const resetState = () => {
    setFiles([]);
    setProgress(0);
    setUploading(false);
    setLoading(false);
    setCurrentFileIndex(0);
    setIsFolderUpload(false);
    setModalRfpType(rfpType && rfpType !== 'all' ? rfpType : '');
    setModalPartnerName(partnerName || '');
    setRfpIssuer('');
    setPartnerNameError(false);
    setRfpIssuerError(false);
    onClose();
  };


  const handleFileChange = async (event) => {
    const uploadedFiles = Array.from(event.target.files);
    
    // Check if files are from a folder
    const isFromFolder = uploadedFiles.some(file => file.webkitRelativePath && file.webkitRelativePath.includes('/'));
    
    // For single file upload (not from folder), reject .xlsx and .csv files
    if (!isFromFolder) {
      const excelOrCsvFiles = uploadedFiles.filter(file => {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        return extension === '.xlsx' || extension === '.csv';
      });
      if (excelOrCsvFiles.length > 0) {
        showSnackbar('XLSX and CSV files cannot be uploaded as single files. Please use folder upload instead.', 'error');
        event.target.value = null;
        return;
      }
    }
    
    // Validate file sizes
    const invalidFiles = uploadedFiles.filter(file => {
      const fileSizeInMB = file.size / (1024 * 1024);
      return fileSizeInMB > 20;
    });

    if (invalidFiles.length > 0) {
      showSnackbar(`${invalidFiles.length} file(s) exceed 20MB limit. Please select smaller files.`, 'error');
      event.target.value = null;
      return;
    }

    // Validate file types - different extensions for folder vs single file upload
    const validExtensions = isFromFolder ? ['.pdf','.docx','.xlsx','.csv'] : ['.pdf','.docx'];
    const invalidTypeFiles = uploadedFiles.filter(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return !validExtensions.includes(extension);
    });

    if (invalidTypeFiles.length > 0) {
      if (isFromFolder) {
        showSnackbar(`${invalidTypeFiles.length} file(s) have invalid format. Only PDF, DOCX, XLSX, CSV files are allowed.`, 'error');
      } else {
        showSnackbar(`${invalidTypeFiles.length} file(s) have invalid format. Only PDF and DOCX files are allowed.`, 'error');
      }
      event.target.value = null;
      return;
    }

    if (uploadedFiles.length > 0) {
      setIsFolderUpload(isFromFolder);
      setFiles(uploadedFiles);
      
      if (isFromFolder) {
        showSnackbar(`Selected folder with ${uploadedFiles.length} file(s). All files will be uploaded.`, 'success');
      } else {
        showSnackbar(`${uploadedFiles.length} file(s) selected and ready to upload.`, 'success');
      }
    }
  };

  const handleSubmit = async () => {
    if (files.length > 0) {
      if (!modalRfpType || modalRfpType.trim() === '') {
        showSnackbar('Please select RFP Type before uploading files.', 'error');
        return;
      }

      if (!rfpIssuer || rfpIssuer.trim() === '') {
        setRfpIssuerError(true);
        showSnackbar('Please select RFP Issuer before uploading files.', 'error');
        return;
      } else {
        setRfpIssuerError(false);
      }
      
      // Validate: If RFP Issuer is "Others", Partner Name is mandatory
      if (rfpIssuer === 'Others' && (!modalPartnerName || modalPartnerName.trim() === '')) {
        setPartnerNameError(true);
        showSnackbar('Enter partner Name', 'error');
        return;
      } else {
        setPartnerNameError(false);
      }
      
      setUploading(true);
      setLoading(true);
      setProgress(0);
      setIsCloseDisabled(true);
      setCurrentFileIndex(0);
      
      simulateFileUpload();
      await uploadFiles();
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="modal-title" aria-describedby="modal-description">
      <Box sx={getModalStyle()} className='fileuploadModal'>
        <GlobalStyles styles={`@media (min-width: 1024px){ .fileuploadText { font-size: 12.8px !important; } }`} />
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
            }}
          >
            <CircularProgress className=' text-primary' />
          </Box>
        )}

        <IconButton
          aria-label="close"
          onClick={resetState}
          disabled={isCloseDisabled}
          sx={{
            position: 'absolute',
            right: 8,
            top: 5,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" component="h2" textAlign="left" className='fw-bold fs-6 pb-1 addRfpText' sx={{ marginTop: '-13px', fontSize: '16px' }}>
          Upload Winning RFP Files
        </Typography>

        <Divider />

        <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
          <Grid item xs={12} sm={rfpIssuer === 'Others' ? 4 : 6}>
            <FormControl 
              fullWidth
              size="small" 
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '13px',
                  fontWeight: 500,
                }
              }}
            >
              <InputLabel id="modal-rfp-type-select-label">RFP Type *</InputLabel>
              <Select
                labelId="modal-rfp-type-select-label"
                value={modalRfpType}
                onChange={(e) => setModalRfpType(e.target.value)}
                label="RFP Type *"
                sx={{
                  borderRadius: '8px',
                }}
                IconComponent={modalRfpType ? () => null : undefined}
                endAdornment={modalRfpType && (
                  <InputAdornment position="end">
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalRfpType('');
                      }}
                      sx={{ padding: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      marginTop: '8px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    },
                  },
                }}
              >
                <MenuItem value='solutioning'>Solutioning</MenuItem>
                <MenuItem value='staffing'>Staffing</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={rfpIssuer === 'Others' ? 4 : 6}>
            <FormControl 
              fullWidth
              size="small" 
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '13px',
                  fontWeight: 500,
                }
              }}
              error={rfpIssuerError}
            >
              <InputLabel id="modal-rfp-issuer-select-label">RFP Issuer *</InputLabel>
              <Select
                labelId="modal-rfp-issuer-select-label"
                value={rfpIssuer}
                onChange={(e) => {
                  setRfpIssuer(e.target.value);
                  // Clear partner name error when RFP Issuer changes
                  if (partnerNameError) {
                    setPartnerNameError(false);
                  }
                  if (rfpIssuerError) {
                    setRfpIssuerError(false);
                  }
                }}
                label="RFP Issuer *"
                disabled={loadingIssuers}
                sx={{
                  borderRadius: '8px',
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      marginTop: '8px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    },
                  },
                }}
              >
                {rfpIssuersData?.rfp_issuers
                  ?.filter((item, index, self) => 
                    index === self.findIndex((t) => t.issuer === item.issuer)
                  )
                  .map((item) => (
                    <MenuItem key={item.id} value={item.issuer}>
                      {item.issuer}
                    </MenuItem>
                  ))}
                <MenuItem value="Others">Others</MenuItem>
              </Select>
              {rfpIssuerError && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  RFP Issuer is required
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          {rfpIssuer === 'Others' && (
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Partner Name"
                value={modalPartnerName}
                onChange={(e) => {
                  setModalPartnerName(e.target.value);
                  if (partnerNameError && e.target.value.trim() !== '') {
                    setPartnerNameError(false);
                  }
                }}
                required={true}
                error={partnerNameError}
                helperText={partnerNameError ? 'Enter Partner Name' : ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    fontWeight: 500,
                  }
                }}
              />
            </Grid>
          )}
        </Grid>

        {loadingIssuers && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress size={20} />
            <Typography variant="caption" sx={{ ml: 1 }}>Loading RFP Issuers...</Typography>
          </Box>
        )}

        <Box sx={{
          backgroundColor: 'rgba(215, 233, 245)',
          border: '1px dashed blue',
          borderRadius: '5px',
          textAlign: 'center',
          p: files.length > 0 ? 3 : 5,
          mt: 1,
          mb: 0.5,
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          mx: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
          className='fileuploadBox docuploadBox'
        >
          <Box onClick={() => document.getElementById('raised-button-folder').click()}>
            <img src={PDFICON} alt='PDFICON' width={'50px'} height={'60px'} style={{ cursor: 'pointer' }} className='pb-2 addRfpFile' />
          </Box>

          <Typography className='fileuploadText' style={{ 
            textAlign: "center", 
            fontSize: '12px',
            width: '100%',
            maxWidth: '100%',
            wordWrap: 'break-word'
          }}>
            {files.length === 0 && (
              <span>
                Drag and Drop your Files or Folder here, or{' '}
                <label htmlFor="raised-button-file" className='fileuploadTextsub' style={{ color: '#007bff', cursor: 'pointer', fontSize: '12px' }}>
                  Browse Files
                </label>
                {' '}or{' '}
                <label htmlFor="raised-button-folder" className='fileuploadTextsub' style={{ color: '#007bff', cursor: 'pointer', fontSize: '12px' }}>
                  Browse Folder
                </label>
              </span>
            )}
            {files.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                mb: 3,
                width: '100%',
                maxWidth: 'calc(100% - 32px)',
                boxSizing: 'border-box',
                textAlign: 'left'
              }}>
                {isFolderUpload ? (

<>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, flexShrink: 0, textAlign: 'left' }}>
                      Selected {files.length} files (all files will be uploaded):
                    </Typography>
                    <Box sx={{ 
                      overflowY: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '16px',
                      backgroundColor: '#fafafa',
                      maxHeight: files.length > 5 ? '300px' : 'auto',
                      fontFamily: 'Arial, sans-serif',
                      textAlign: 'left',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#888',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#555',
                      }
                    }}>
                    {(() => {
                      // Group files by folder structure
                      const folderStructure = {};
                      files.forEach((file, index) => {
                        const pathParts = file.webkitRelativePath ? file.webkitRelativePath.split('/') : [file.name];
                        let current = folderStructure;
                        
                        for (let i = 0; i < pathParts.length - 1; i++) {
                          const folderName = pathParts[i];
                          if (!current[folderName]) {
                            current[folderName] = { type: 'folder', children: {}, files: [] };
                          }
                          current = current[folderName].children;
                        }
                        
                        const fileName = pathParts[pathParts.length - 1];
                        current[fileName] = { type: 'file', file, index };
                      });

                      const renderTree = (structure, level = 0, parentPath = '') => {
                        const items = [];
                        
                        Object.entries(structure).forEach(([name, item], idx, arr) => {
                          const isLast = idx === arr.length - 1;
                          const indent = '│  '.repeat(level);
                          const connector = isLast ? '└── ' : '├── ';
                          const fullPath = parentPath ? `${parentPath}/${name}` : name;
                          
                          if (item.type === 'folder') {
                            // Folder
                            items.push(
                              <Box key={`folder-${name}`} sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                mb: 0.5,
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '12px',
                                overflow: 'hidden'
                              }}>
                                <Typography sx={{ 
                                  color: '#2196f3',
                                  fontFamily: 'Arial, sans-serif',
                                  fontSize: '12px',
                                  whiteSpace: 'pre',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  flex: 1,
                                  minWidth: 0,
                                  textAlign: 'left'
                                }}
                                title={`${indent}${connector}📁 ${name}`}
                                >
                                  {indent}{connector}📁 {name}
                                </Typography>
                              </Box>
                            );
                            
                            // Render folder contents
                            items.push(...renderTree(item.children, level + 1, fullPath));
                          } else {
                            // File
                            items.push(
                              <Box 
                                key={`file-${name}`} 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  mb: 0.5,
                                  fontFamily: 'Arial, sans-serif',
                                  fontSize: '12px',
                                  opacity: (loading || uploading) ? 0.5 : 1
                                }}
                              >
                                <Typography sx={{ 
                                  color: '#333',
                                  fontFamily: 'Arial, sans-serif',
                                  fontSize: '12px',
                                  whiteSpace: 'pre',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  flex: 1,
                                  minWidth: 0,
                                  textAlign: 'left'
                                }}
                                title={`${indent}${connector}📄 ${name}`}
                                >
                                  {indent}{connector}📄 {name}
                                </Typography>
                              </Box>
                            );
                          }
                        });
                        
                        return items;
                      };

                      return renderTree(folderStructure);
                    })()}
                    </Box>
                  </>
                ) : (
                  // Simple file list for single files (no selection, no folder structure)
                  <Box sx={{ 
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '16px',
                    backgroundColor: '#fafafa',
                    maxHeight: files.length > 5 ? '300px' : 'auto',
                    fontFamily: 'Arial, sans-serif',
                    textAlign: 'left',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#888',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: '#555',
                    }
                  }}>
                    {files.map((file, index) => {
                      return (
                        <Box 
                          key={`file-${index}`}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            mb: 0.5,
                            fontFamily: 'Arial, sans-serif',
                            fontSize: '12px',
                            padding: '4px 0'
                          }}
                        >
                          <Typography sx={{ 
                            color: '#333',
                            fontFamily: 'Arial, sans-serif',
                            fontSize: '12px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1,
                            minWidth: 0,
                            textAlign: 'left'
                          }}
                          title={file.name}
                          >
                            📄 {file.name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
            <Typography className='subtextfu' sx={{ color: '#888', fontSize: '9px', textAlign: 'center', mt: files.length > 0 ? 1 : 0, flexShrink: 0 }}>
              Limit 20MB per file. PDF, DOCX Files Only (XLSX and CSV allowed in folder upload only).
            </Typography>
          </Typography>

          <input
            accept=".pdf,.docx"
            style={{ display: 'none' }}
            id="raised-button-file"
            type="file"
            multiple
            onChange={handleFileChange}
          />
          <input
            accept=".pdf,.doc,.docx,.xlsx,.csv"
            style={{ display: 'none' }}
            id="raised-button-folder"
            type="file"
            multiple
            webkitdirectory=""
            onChange={handleFileChange}
          />
        </Box>

        {uploading && (
          <Box sx={{ 
            width: '100%', 
            position: 'sticky',
            bottom: '60px',
            zIndex: 10,
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
            padding: '8px 0'
          }} className='fileuploadBox'>
            <div className='border border-1px solid grey p-2'>
              <Box alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <img src={PDFICON} alt='PDFICON' width={'30px'} height={'42px'} style={{ cursor: 'pointer' }} className=' mx-1' />
                  <Box ml={2}>
                    <div style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Typography className='pdfnamefu' style={{ textDecoration: 'none', width: "200px", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", fontSize: '11px' }}>
                        {files[currentFileIndex]?.name}
                      </Typography>
                      {files.length > 1 && (
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '9px' }}>
                          File {currentFileIndex + 1} of {files.length}
                        </Typography>
                      )}
                    </div>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ width: '100%', marginTop: '15px' }} className='progressbar'>
                <LinearProgress variant="determinate" value={progress} sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4caf50',
                  },
                }} />
                {files.length > 1 && (
                  <Typography variant="caption" sx={{ color: '#666', fontSize: '9px', mt: 0.5, display: 'block' }}>
                    Overall Progress: {Math.round(progress)}%
                  </Typography>
                )}
              </Box>
            </div>
          </Box>
        )}

        {files.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'end', 
            mt: 2,
            mb: 1,
            pt: 2,
            borderTop: '1px solid #e0e0e0',
            flexShrink: 0
          }}>
            <Button 
              color="primary" 
              onClick={handleSubmit} 
              disabled={loading || uploading} 
              className='rounded-2 bg-primary text-white viewButton'
            >
              {uploading ? 'Uploading...' : `Upload (${files.length} files)`}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

const getModalStyle = () => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: '8px',
  boxShadow: 24,
  pt: 5,
  px: 5,
  pb: 3,
  width: '108%',
  maxWidth: '1800px',
  maxHeight: '90vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#555',
  }
});

export default FileUploadModal;

